import { useCurrentPageAccess } from "@/app/router";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { RotateCw } from "lucide-react";
import { useState } from "react";
import { CollectionStatusStats } from "../components/CollectionStatusStats";
import {
  useCollectionErrorColumns,
  useCollectionInProgressColumns,
  useCollectionSuccessColumns,
} from "../hooks/useCollectionStatusColumns";
import {
  useCancelCollectionsMutation,
  useCollectionStatusQuery,
} from "../hooks/useCollectionStatus";
import type { CollectionStatusTab } from "../types/collection-types";
import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import type { SortingState } from "@tanstack/react-table";

export function CollectionsStatusPage() {
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const cancelMutation = useCancelCollectionsMutation();

  // 탭 상태
  const [tab, setTab] = useState<CollectionStatusTab>("success");

  // 페이지네이션 상태
  const [successPage, setSuccessPage] = useState(1);
  const [successPageSize, setSuccessPageSize] = useState(10);
  const [successSorting, setSuccessSorting] = useState<SortingState>([]);
  const [successKeyword, setSuccessKeyword] = useState("");

  const [errorPage, setErrorPage] = useState(1);
  const [errorPageSize, setErrorPageSize] = useState(10);
  const [errorSorting, setErrorSorting] = useState<SortingState>([]);
  const [errorKeyword, setErrorKeyword] = useState("");

  const [inProgressPage, setInProgressPage] = useState(1);
  const [inProgressPageSize, setInProgressPageSize] = useState(10);
  const [inProgressSorting, setInProgressSorting] = useState<SortingState>([]);

  // in-progress filter
  const [transId, setTransId] = useState("");
  const [channel, setChannel] = useState("");

  // 현재 탭의 정렬/페이지 상태를 params로 변환
  const currentSort = tab === "success" ? successSorting : tab === "error" ? errorSorting : inProgressSorting;
  const currentPage = tab === "success" ? successPage : tab === "error" ? errorPage : inProgressPage;
  const currentPageSize = tab === "success" ? successPageSize : tab === "error" ? errorPageSize : inProgressPageSize;
  const currentKeyword = tab === "success" ? successKeyword : tab === "error" ? errorKeyword : undefined;

  // API 데이터 조회
  const { data, isLoading, isError, refetch } = useCollectionStatusQuery({
    tab,
    page: currentPage,
    page_size: currentPageSize,
    sort_by: currentSort[0]?.id,
    sort_desc: currentSort[0]?.desc,
    keyword: currentKeyword || undefined,
    trans_id: tab === "in_progress" && transId ? transId : undefined,
    channel: tab === "in_progress" && channel ? channel : undefined,
  });

  // tables
  const successColumns = useCollectionSuccessColumns();
  const errorColumns = useCollectionErrorColumns();
  const inProgressColumns = useCollectionInProgressColumns();

  const [selectedInProgress, setSelectedInProgress] = useState<
    Array<NonNullable<typeof data>["in_progress_rows"][number]>
  >([]);

  const handleRefresh = async () => {
    await refetch();
  };

  const handleCancelSelected = async () => {
    if (selectedInProgress.length === 0) return;

    await cancelMutation.mutateAsync({
      trans_ids: selectedInProgress.map((row) => row.trans_id),
    });

    setSelectedInProgress([]);
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div>
        <Breadcrumbs />
        <PageHeader
          title="수집 현황"
          description="프리즘에서 수행중이거나 완료된 수집 상태를 조회합니다."
        />
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // 에러 상태
  if (isError || !data) {
    return (
      <div>
        <Breadcrumbs />
        <PageHeader
          title="수집 현황"
          description="프리즘에서 수행중이거나 완료된 수집 상태를 조회합니다."
        />
        <EmptyState
          title="데이터를 불러올 수 없습니다"
          description="오류가 발생했습니다. 다시 시도해주세요."
          action={
            <Button variant="outline" onClick={handleRefresh}>
              다시 시도
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs />
      <PageHeader
        title="수집 현황"
        description="프리즘에서 수행중이거나 완료된 수집 상태를 조회합니다."
        actions={
          <Button variant="outline" onClick={handleRefresh}>
            <RotateCw className="mr-2 h-4 w-4" />
            새로고침
          </Button>
        }
      />

      <div className="space-y-4">
        <CollectionStatusStats summary={data.summary} onSelectTab={setTab} />

        <Tabs value={tab} onValueChange={(v) => setTab(v as CollectionStatusTab)}>
          <TabsList>
            <TabsTrigger value="success">성공한 수집</TabsTrigger>
            <TabsTrigger value="error">실패한 수집</TabsTrigger>
            <TabsTrigger value="in_progress">진행 중인 수집</TabsTrigger>
          </TabsList>

          <TabsContent value="success">
            <DataTable
              columns={successColumns}
              data={data.success_rows}
              totalCount={data.total_count}
              page={successPage}
              pageSize={successPageSize}
              sorting={successSorting}
              onSortingChange={setSuccessSorting}
              onPageChange={setSuccessPage}
              onPageSizeChange={(size) => {
                setSuccessPageSize(size);
                setSuccessPage(1);
              }}
              isLoading={false}
              isError={false}
              toolbar={
                <DataTableToolbar
                  searchValue={successKeyword}
                  onSearchChange={(v) => {
                    setSuccessKeyword(v);
                    setSuccessPage(1);
                  }}
                  showReset={!!successKeyword}
                  onReset={() => {
                    setSuccessKeyword("");
                    setSuccessPage(1);
                  }}
                />
              }
            />
          </TabsContent>

          <TabsContent value="error">
            <DataTable
              columns={errorColumns}
              data={data.error_rows}
              totalCount={data.total_count}
              page={errorPage}
              pageSize={errorPageSize}
              sorting={errorSorting}
              onSortingChange={setErrorSorting}
              onPageChange={setErrorPage}
              onPageSizeChange={(size) => {
                setErrorPageSize(size);
                setErrorPage(1);
              }}
              isLoading={false}
              isError={false}
              toolbar={
                <DataTableToolbar
                  searchValue={errorKeyword}
                  onSearchChange={(v) => {
                    setErrorKeyword(v);
                    setErrorPage(1);
                  }}
                  showReset={!!errorKeyword}
                  onReset={() => {
                    setErrorKeyword("");
                    setErrorPage(1);
                  }}
                />
              }
            />
          </TabsContent>

          <TabsContent value="in_progress">
            <DataTable
              columns={inProgressColumns}
              data={data.in_progress_rows}
              totalCount={data.total_count}
              page={inProgressPage}
              pageSize={inProgressPageSize}
              sorting={inProgressSorting}
              onSortingChange={setInProgressSorting}
              onPageChange={setInProgressPage}
              onPageSizeChange={(size) => {
                setInProgressPageSize(size);
                setInProgressPage(1);
              }}
              isLoading={false}
              isError={false}
              enableRowSelection
              getRowId={(row) => row.trans_id}
              onRowSelectionChange={setSelectedInProgress}
              toolbar={
                <DataTableToolbar
                  filters={
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">trans_id:</span>
                        <Input
                          value={transId}
                          onChange={(e) => {
                            setTransId(e.target.value);
                            setInProgressPage(1);
                          }}
                          className="h-9 w-[240px]"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">channel:</span>
                        <Input
                          value={channel}
                          onChange={(e) => {
                            setChannel(e.target.value);
                            setInProgressPage(1);
                          }}
                          className="h-9 w-[180px]"
                        />
                      </div>
                    </div>
                  }
                  actions={
                    <Button
                      variant="destructive"
                      disabled={!canWriteCurrentPage || selectedInProgress.length === 0 || cancelMutation.isPending}
                      onClick={handleCancelSelected}
                    >
                      {cancelMutation.isPending ? "취소 중..." : "취소"}
                    </Button>
                  }
                />
              }
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
